/**
 * ErrorTelemetry.ts
 *
 * Module-level singleton that bridges two React lifecycle boundaries:
 *   1. The authenticated user id (set when Shell confirms auth).
 *   2. AppErrorBoundary.componentDidCatch (a class component with no hooks).
 *
 * Usage:
 *   Shell.tsx → setCurrentUserId(user.id)  after auth resolves
 *   AppErrorBoundary → reportError(error, info)  inside componentDidCatch
 *
 * The POST is best-effort — if the network is down or the API is unreachable,
 * the error is still shown to the user; we never throw from the reporter.
 */

let _currentUserId: number | null = null;

/** Called by Shell once the authenticated user is known. */
export function setCurrentUserId(id: number | null): void {
  _currentUserId = id;
}

export interface ErrorReport {
  message: string;
  stack: string;
  componentStack: string;
  componentName: string;
  url: string;
  route: string;
  userAgent: string;
  userId: number | null;
  timestamp: string;
  buildId: string;
}

/**
 * Extract the first named React component from a component stack string.
 * e.g. "\n    at Shell (Shell.tsx:42:8)" → "Shell"
 */
export function extractComponentName(componentStack: string): string {
  const line = componentStack
    .split('\n')
    .map(l => l.trim())
    .find(l => l.startsWith('at ') && !l.includes('(native)') && !l.includes('ErrorBoundary'));
  if (!line) return 'Unknown';
  return line.replace(/^at\s+/, '').split(/[\s(]/)[0] ?? 'Unknown';
}

/**
 * Fire-and-forget: POST a full error report to /api/client-errors.
 * Never throws. Never awaited — call from componentDidCatch.
 */
export function reportError(error: Error | null, componentStack: string): void {
  try {
    const now = new Date().toISOString();
    const report: ErrorReport = {
      message:        error?.message ?? '(no message)',
      // Never send raw stack to UI — only to the server log
      stack:          (error?.stack ?? '').slice(0, 3000),
      componentStack: componentStack.slice(0, 2000),
      componentName:  extractComponentName(componentStack),
      url:            typeof window !== 'undefined' ? window.location.href : '',
      route:          typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent:      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 250) : '',
      userId:         _currentUserId,
      timestamp:      now,
      buildId:        '2.29.0',
    };

    fetch('/api/client-errors', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(report),
    }).catch(() => { /* never throw from the error reporter */ });
  } catch {
    // If even building the report fails, swallow silently.
  }
}
